import dayjs from 'dayjs';
import { Calendar } from 'primereact/calendar';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tooltip } from 'primereact/tooltip';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import { Product } from '../../../../../../../enums/product';
import useAxiosHook from '../../../../../../../hooks/useAxiosHook';
import usePrevious from '../../../../../../../hooks/usePrevious';
import { WithPagination } from '../../../../../../../types/api';
import { LabelValue } from '../../../../../../../types/options';
import { debounceTimeout } from '../../../../../../../utils/constants/misc';
import { invalidDecimalPointCharactersRegex } from '../../../../../../../utils/constants/regex';
import { tryString } from '../../../../../../../utils/helpers/parse';
import AutoCompleteInput from '../../../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../../../Forms/ReactHookForm/FieldWithErrors';
import { Order } from '../../../../Orders.functions';
import {
  FallbackValues,
  RedemptionFieldValidation,
  RedemptionReceiverFieldValidation,
  TimeSlot,
  getOrderPackagingOptions,
  getTimeSlotOptions,
  validateRedemptionField,
  validateRedemptionReceiverField,
} from '../../CreateEditDialog.functions';
import { LetterAttributes } from './LetterAttributes';
import { PackageAttributes } from './PackageAttributes';

type Props = {
  isTabs?: boolean;
  orderIndex?: number;
  isConstant?: boolean;
  isEditDialog?: boolean;
};

function AttributesTab({
  isTabs,
  orderIndex,
  isConstant,
  isEditDialog,
}: Props) {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const values = watch();
  const [relatedOrderFilter, setRelatedOrderFilter] = useState('');

  const debouncedRelatedOrderFilter = useDebounce(
    relatedOrderFilter,
    debounceTimeout
  );

  const {
    data: relatedOrderOptionsData,
    isLoading: isRelatedOrderOptionsDataLoading,
  } = useAxiosHook<WithPagination<Order[]>>(
    `/orders?serial=${debouncedRelatedOrderFilter}`,
    { skipWhen: !debouncedRelatedOrderFilter }
  );

  const relatedOrderOptions = useMemo<LabelValue<string>[]>(
    () =>
      relatedOrderOptionsData?.data.map((o) => ({
        label: o.seriski_broj,
        value: String(o.id),
      })) ?? [],
    [relatedOrderOptionsData]
  );

  const {
    data: returnDocumentOptionsData,
    isLoading: isReturnDocumentOptionsDataLoading,
  } = useAxiosHook<any>('/return-document-types');

  const returnDocumentOptions = useMemo<LabelValue<string>[]>(
    () =>
      returnDocumentOptionsData?.map((o: any) => ({
        label: o.name,
        value: o.id,
      })) ?? [],
    [returnDocumentOptionsData]
  );

  const orderPackagingOptions = useMemo(() => getOrderPackagingOptions(t), [t]);
  const timeSlotOptions = useMemo(() => getTimeSlotOptions(t), [t]);

  const { data: relatedOrder } = useAxiosHook<Order>(
    `/orders/${values?.reference_id}`,
    {
      skipWhen: !values?.reference_id || !!relatedOrderFilter,
    }
  );

  const prevRelatedOrder = usePrevious(relatedOrder);

  useEffect(() => {
    if (!relatedOrder || prevRelatedOrder || relatedOrderFilter) {
      return;
    }

    setRelatedOrderFilter(relatedOrder.seriski_broj);
  }, [prevRelatedOrder, relatedOrder, relatedOrderFilter]);

  const isTimeslot = values?.delivery_term_id == TimeSlot.TimeSlot;

  const isRedemptionFilledOut = parseFloat(values?.otkup ?? '') > 0;

  const redemptionFieldValidation = useMemo(
    () => validateRedemptionField(values?.klient_od_id, values?.vrednost),
    [values]
  );

  const isRedemptionDisabled =
    redemptionFieldValidation !== RedemptionFieldValidation.Valid;

  const redemptionReceiverFieldValidation = useMemo(
    () =>
      validateRedemptionReceiverField(
        values?.klient_od_id,
        values?.otkup,
        values?.vrednost,
        values?.orderer
      ),
    [values]
  );

  const isRedemptionReceiverDisabled =
    redemptionReceiverFieldValidation !==
    RedemptionReceiverFieldValidation.Valid;

  const isInsuredAmountDisabled =
    values?.proizvod_id === Product.Package && isRedemptionFilledOut;

  const insuredAmountTooltip = t(
    'Only one of redemption or value can be present at a time.'
  );

  return (
    <div className="special-tab p-grid">
      <Tooltip
        target=".disabled-value-tooltip"
        position="left"
        style={isRedemptionFilledOut ? {} : { display: 'none' }}
      />

      <Tooltip
        target=".disabled-redemption-tooltip"
        position="left"
        style={isRedemptionDisabled ? {} : { display: 'none' }}
      />

      <Tooltip
        target=".disabled-redemption-receiver-tooltip"
        position="left"
        style={isRedemptionReceiverDisabled ? {} : { display: 'none' }}
      />

      <Tooltip
        target=".disabled-insured-amount-tooltip"
        position="left"
        style={isInsuredAmountDisabled ? {} : { display: 'none' }}
      />

      {orderIndex !== 0 && isConstant && <div className="constant" />}

      {isTabs && (
        <>
          <FieldWithErrors name="proizvod_id" label={t('Order type')}>
            <Controller
              name="proizvod_id"
              render={({ field }) => (
                <Dropdown
                  id="proizvod_id"
                  name="proizvod_id"
                  options={[
                    { label: t('Package'), value: Product.Package },
                    { label: t('Document'), value: Product.Document },
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  className="data-cy-proizvod_id"
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="orderer" label={t('Orderer')}>
            <Controller
              name="orderer"
              render={({ field }) => (
                <Dropdown
                  id="orderer"
                  name="orderer"
                  options={[
                    { label: t('Sender is orderer'), id: 1 },
                    { label: t('Recipient is orderer'), id: 2 },
                    { label: t('Third party orderer'), id: 3 },
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  className="data-cy-orderer"
                />
              )}
            />
          </FieldWithErrors>
        </>
      )}

      <FieldWithErrors
        name="vrednost"
        label={t('Insured amount')}
        className="p-col-6"
      >
        <div
          className="disabled-insured-amount-tooltip"
          data-pr-tooltip={insuredAmountTooltip}
        >
          <Controller
            name="vrednost"
            render={({ field }) => (
              <InputText
                id="vrednost"
                name="vrednost"
                value={field.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(
                    invalidDecimalPointCharactersRegex,
                    ''
                  );

                  field.onChange(value);

                  if (parseFloat(value) > 0) {
                    setValue(
                      'otkup',
                      isEditDialog ? FallbackValues.Redemption : ''
                    );
                  }
                }}
                disabled={isInsuredAmountDisabled}
                data-cy="vrednost"
              />
            )}
          />
        </div>
      </FieldWithErrors>

      <FieldWithErrors
        name="kolicina"
        label={t('Quantity')}
        className="p-col-6"
      >
        <Controller
          name="kolicina"
          render={({ field }) => (
            <InputText
              id="kolicina"
              name="kolicina"
              value={field.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(
                  e.target.value?.replace(
                    invalidDecimalPointCharactersRegex,
                    ''
                  )
                );
              }}
              data-cy="kolicina"
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors name="tezina" label={t('Weight')} className="p-col-6">
        <Controller
          name="tezina"
          render={({ field }) => (
            <InputText
              id="tezina"
              name="tezina"
              value={field.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                field.onChange(
                  e.target.value?.replace(
                    invalidDecimalPointCharactersRegex,
                    ''
                  )
                );
              }}
              data-cy="tezina"
            />
          )}
        />
      </FieldWithErrors>

      {values?.proizvod_id === Product.Package && (
        <PackageAttributes
          isRedemptionReceiverDisabled={isRedemptionReceiverDisabled}
          redemptionReceiverFieldValidation={redemptionReceiverFieldValidation}
        />
      )}

      {values?.proizvod_id === Product.Document && (
        <LetterAttributes isRedemptionFilledOut={isRedemptionFilledOut} />
      )}

      <FieldWithErrors
        name="reference1"
        label={t('Reference No. 1')}
        className="p-col-6"
      >
        <Controller
          name="reference1"
          render={({ field }) => (
            <InputText
              id="reference1"
              name="reference1"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="reference2"
        label={t('Reference No. 2')}
        className="p-col-6"
      >
        <Controller
          name="reference2"
          render={({ field }) => (
            <InputText
              id="reference2"
              name="reference2"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="reference_id"
        label={t('Related Order')}
        className="p-col-6"
      >
        <Controller
          name="reference_id"
          render={({ field }) => (
            <AutoCompleteInput
              id="reference_id"
              name="reference_id"
              options={relatedOrderOptions}
              value={tryString(field.value) ?? null}
              filterValue={relatedOrderFilter}
              onFilterChange={setRelatedOrderFilter}
              onSelectionChange={(newValue) => field.onChange(newValue)}
              isLoading={isRelatedOrderOptionsDataLoading}
              placeholder={t('Serial No.')}
              filterDataCy="reference_id"
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="delivery_term_id"
        label={t('Timeframe')}
        className="p-col-6"
      >
        {' '}
        <Controller
          name="delivery_term_id"
          render={({ field }) => (
            <Dropdown
              id="delivery_term_id"
              name="delivery_term_id"
              options={timeSlotOptions}
              value={field.value}
              onChange={(e: DropdownChangeParams) => {
                field.onChange(e.value);
                if (e.value === 1) {
                  setValue('terminski_datum', dayjs().toDate());
                } else if (e.value === 2) {
                  setValue('terminski_datum', dayjs().add(1, 'day').toDate());
                } else if (e.value === 3) {
                  setValue('terminski_datum', dayjs().add(3, 'day').toDate());
                }
              }}
              filter
              filterPlaceholder={t('Search')}
              placeholder={t('Select')}
              className="data-cy-klient_od_id"
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="terminski_datum"
        label={t('Delivery time')}
        className="p-col-6"
      >
        <div className="daily-timeframe p-grid">
          <div className="p-col">
            <Controller
              name="terminski_datum"
              render={({ field }) => (
                <Calendar
                  id="terminski_datum"
                  name="terminski_datum"
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!isTimeslot}
                  dateFormat="dd/mm/yy"
                  placeholder={t('Date')}
                />
              )}
            />
          </div>

          {isTimeslot && (
            <div className="daily-timeframe p-col-6">
              <FieldWithErrors name="terminsko_vreme_od" label={t('From')}>
                <Controller
                  name="terminsko_vreme_od"
                  render={({ field }) => (
                    <Calendar
                      id="terminsko_vreme_od"
                      name="terminsko_vreme_od"
                      value={field.value}
                      onChange={field.onChange}
                      timeOnly
                      showTime
                    />
                  )}
                />
              </FieldWithErrors>
              <div className="divider"> - </div>
              <FieldWithErrors name="terminsko_vreme_do" label={t('To')}>
                <Controller
                  name="terminsko_vreme_do"
                  render={({ field }) => (
                    <Calendar
                      id="terminsko_vreme_do"
                      name="terminsko_vreme_do"
                      value={field.value}
                      onChange={field.onChange}
                      timeOnly
                      showTime
                    />
                  )}
                />
              </FieldWithErrors>
            </div>
          )}
        </div>
      </FieldWithErrors>

      <FieldWithErrors
        name="adresnica_service_packaging_id"
        label={t('Order service packaging')}
        className="p-col-6"
      >
        <Controller
          name="adresnica_service_packaging_id"
          render={({ field }) => (
            <Dropdown
              id="adresnica_service_packaging_id"
              name="adresnica_service_packaging_id"
              options={orderPackagingOptions}
              value={field.value}
              onChange={field.onChange}
              showClear
              placeholder={t('No packaging')}
              className="data-cy-adresnica_service_packaging_id"
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="povraten_dokument"
        label={t('Return Document')}
        className="p-col-6"
      >
        <Controller
          name="povraten_dokument"
          render={({ field }) => (
            <InputSwitch
              id="povraten_dokument"
              name="povraten_dokument"
              checked={Boolean(field.value)}
              onChange={field.onChange}
            />
          )}
        />
      </FieldWithErrors>

      {Boolean(values?.povraten_dokument) && (
        <FieldWithErrors
          name="return_document_type_id"
          label={t('Type of return document')}
          className="p-col-6"
        >
          <Controller
            name="return_document_type_id"
            render={({ field }) => (
              <Dropdown
                id="return_document_type_id"
                name="return_document_type_id"
                options={returnDocumentOptions}
                value={field.value}
                onChange={field.onChange}
                showClear
                placeholder={
                  isReturnDocumentOptionsDataLoading
                    ? t('Loading...')
                    : t('No packaging')
                }
                className="data-cy-return_document_type_id"
              />
            )}
          />
        </FieldWithErrors>
      )}

      {values?.replacement_shipment !== 2 && (
        <FieldWithErrors
          name="replacement_shipment"
          label={t('Replacement shipment')}
          className="p-col-6"
        >
          <Controller
            name="replacement_shipment"
            render={({ field }) => (
              <InputSwitch
                id="replacement_shipment"
                name="replacement_shipment"
                checked={Boolean(field.value)}
                onChange={field.onChange}
              />
            )}
          />
        </FieldWithErrors>
      )}

      <FieldWithErrors
        name="fragile"
        label={t('Is fragile ?')}
        className="p-col-6"
      >
        <Controller
          name="fragile"
          render={({ field }) => (
            <InputSwitch
              id="fragile"
              name="fragile"
              checked={Boolean(field.value)}
              onChange={field.onChange}
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="two_man_delivery"
        label={t('Is two man delivery ?')}
        className="p-col-6 two_man_delivery"
      >
        <Controller
          name="two_man_delivery"
          render={({ field }) => (
            <InputSwitch
              id="two_man_delivery"
              name="two_man_delivery"
              checked={Boolean(field.value)}
              onChange={field.onChange}
            />
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors
        name="komentar"
        label={t('Note')}
        className="p-col-12 note"
      >
        <Controller
          name="komentar"
          render={({ field }) => (
            <InputTextarea
              id="komentar"
              name="komentar"
              value={field.value}
              onChange={field.onChange}
              style={{ maxHeight: 100, overflowY: 'auto' }}
              data-cy="comment"
            />
          )}
        />
      </FieldWithErrors>
    </div>
  );
}

export default AttributesTab;
